import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { StatusBadge } from '@truecare/ui';
import type {
  AdminCaregiverListItem,
  AdminCaregiverListResponse,
} from '@truecare/schemas';
import { unwrap } from '@truecare/api-client';
import { api } from '../../api';
import { adminBeforeLoad } from '../../lib/admin-before-load';
import { AdminLayout } from '../../components/AdminLayout';
import {
  AdminTable,
  type AdminTableColumn,
} from '../../components/AdminTable';
import {
  AdminListFilters,
  type FilterField,
} from '../../components/AdminListFilters';

/**
 * Phase 14b-2 — Caregivers list. Largest persona by action surface;
 * the list itself is read-only browse. Filters per 14b-2-4: status +
 * role + bankingVerification.
 */

const SearchSchema = z.object({
  status: z.string().optional(),
  role: z.string().optional(),
  bankingVerification: z.string().optional(),
  // Audit H7 — the credential.expiring_30d_count KPI drills here with this flag.
  hasExpiringCredential: z.coerce.boolean().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(25),
});

type Search = z.infer<typeof SearchSchema>;

export const Route = createFileRoute('/caregivers/')({
  beforeLoad: () => adminBeforeLoad('/caregivers'),
  validateSearch: (search) => SearchSchema.parse(search),
  component: CaregiversListPage,
});

const STATUS_OPTIONS = [
  { value: 'APPLYING', label: 'Applying' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  // Phase 5g — approved, completing banking + SIN + contract (auto-activates
  // when done). Surfaced here so a stuck onboarding is chase-able.
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'TERMINATED', label: 'Terminated' },
];

const ROLE_OPTIONS = [
  { value: 'PSW', label: 'PSW' },
  { value: 'RPN', label: 'RPN' },
  { value: 'RN', label: 'RN' },
  { value: 'COMPANION', label: 'Companion' },
  { value: 'DSW', label: 'DSW' },
  { value: 'COOK', label: 'Cook / Dietary Aide' },
];

// 14b-2 post-audit W2 — label "Awaiting first review" (not "Pending
// verification") so operators don't conflate this filter with the
// 11b admin queue at /admin/banking/pending-verification, which uses
// a SUPERSET definition (submitted+!verified — includes rejected-
// pending-resubmission rows). Here `pending` = submitted+!verified+
// !rejected (first-review queue only).
const BANKING_OPTIONS = [
  { value: 'pending', label: 'Awaiting first review' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected (not yet resubmitted)' },
  { value: 'none', label: 'Not submitted' },
];

const FILTER_FIELDS: FilterField[] = [
  { name: 'status', label: 'Status', type: 'select', options: STATUS_OPTIONS },
  { name: 'role', label: 'Role', type: 'select', options: ROLE_OPTIONS },
  {
    name: 'bankingVerification',
    label: 'Banking',
    type: 'select',
    options: BANKING_OPTIONS,
  },
];

// Column order (Phase 17 M2): the primary decision columns (Status, Banking)
// precede the wide Email column so they stay on-screen on a phone-width table;
// Email — the widest, least scan-urgent column — sits last.
const COLUMNS: AdminTableColumn<AdminCaregiverListItem>[] = [
  {
    key: 'legalSurname',
    header: 'Name',
    render: (r) =>
      `${r.preferredName ?? r.legalGivenName} ${r.legalSurname}`,
  },
  {
    key: 'status',
    header: 'Status',
    width: 'w-32',
    render: (r) => <StatusBadge status={r.status} />,
  },
  {
    key: 'bankingVerification',
    header: 'Banking',
    width: 'w-28',
    sortable: false,
    render: (r) =>
      r.bankingVerification === 'none' ? (
        <span className="text-ink-muted">—</span>
      ) : (
        <StatusBadge status={r.bankingVerification} />
      ),
  },
  {
    key: 'declaredRoleCodes',
    header: 'Roles',
    width: 'w-32',
    sortable: false,
    render: (r) =>
      r.declaredRoleCodes.length > 0 ? (
        r.declaredRoleCodes.join(', ')
      ) : (
        <span className="text-ink-muted">—</span>
      ),
  },
  {
    key: 'createdAt',
    header: 'Applied',
    width: 'w-32',
    render: (r) => new Date(r.createdAt).toLocaleDateString('en-CA'),
  },
  { key: 'email', header: 'Email' },
];

function CaregiversListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const query = useQuery<AdminCaregiverListResponse>({
    queryKey: ['admin', 'caregivers', search],
    queryFn: async () =>
      (await unwrap(
        api.GET('/api/v1/admin/caregivers', {
          params: {
            query: {
              status: search.status,
              role: search.role,
              bankingVerification: search.bankingVerification,
              hasExpiringCredential: search.hasExpiringCredential,
              page: search.page,
              pageSize: search.limit,
            },
          },
        } as never),
      )) as unknown as AdminCaregiverListResponse,
  });

  function updateSearch(next: Partial<Search>): void {
    navigate({ search: { ...search, ...next } as Search });
  }

  return (
    <AdminLayout>
      <h1 className="font-heading text-3xl text-forest md:text-4xl">Care professionals</h1>
      <p className="mt-1 text-sm text-ink-muted">
        PSW, RPN, RN, and Companion contractors. Click a row to open vetting,
        banking, and account actions.
      </p>

      {/* AdminListFilters renders its own bordered bar — no outer Card wrapper
          (Phase 17 M5: avoid the box-in-box above the table). */}
      <div className="mt-6">
        <AdminListFilters
          fields={FILTER_FIELDS}
          values={{
            status: search.status,
            role: search.role,
            bankingVerification: search.bankingVerification,
          }}
          onChange={(next) =>
            updateSearch({ ...next, page: 1 } as Partial<Search>)
          }
        />
      </div>

      <div className="mt-6" data-testid="caregivers-list-table">
        <AdminTable<AdminCaregiverListItem>
          columns={COLUMNS}
          rows={query.data?.items ?? []}
          isLoading={query.isPending}
          isError={query.isError}
          emptyMessage="No care professionals match these filters."
          page={search.page}
          limit={search.limit}
          totalCount={query.data?.total ?? 0}
          onPageChange={(page) => updateSearch({ page })}
          onLimitChange={(limit) => updateSearch({ limit, page: 1 })}
          onRowClick={(row) =>
            navigate({ to: '/caregivers/$id', params: { id: row.id } })
          }
        />
      </div>
    </AdminLayout>
  );
}
